import React, { useState, useCallback, useMemo } from "react"
import SpinWheel, { toSlices, DEFAULT_SLOT_COUNT, PLACEHOLDER_SLOTS } from "../shared/SpinWheel"
import ResultDisplay from "../shared/ResultDisplay"
import EmptyState from "./EmptyState"
import ColorsSection from "./ColorsSection"
import useRodgColors from "./hooks/useRodgColors"
import useRodgSelections from "./hooks/useRodgSelections"
import { stripAugmentation } from "../../helpers/choices"
import { loadWinner, saveWinner, clearWinner, markDone } from "./helpers/storage"

const extractName = (text) => stripAugmentation(text.replace(/^\d+\.\s*/, ""))

const formatSliceText = (item, i) => {
  const suffix = item.meta || (item.wait_minutes ? `${item.wait_minutes} min` : null)
  return suffix ? `${i + 1}. ${item.name} (${suffix})` : `${i + 1}. ${item.name}`
}

const Wheel = () => {
  const { items, removeItem, visibleItems } = useRodgSelections()
  const { customColors, handleColorChange, handleResetAllColors } = useRodgColors({
    resetOnMount: items.length === 0,
  })
  const isEmpty = visibleItems.length === 0

  const choices = isEmpty ? PLACEHOLDER_SLOTS : visibleItems.map((item) => item.name)
  const slices = useMemo(
    () =>
      isEmpty
        ? toSlices(Array(DEFAULT_SLOT_COUNT).fill(""), customColors)
        : toSlices(visibleItems.map(formatSliceText), customColors),
    [isEmpty, visibleItems, customColors]
  )

  const [result, setResult] = useState(() => {
    if (isEmpty) return null
    const winnerName = loadWinner()
    if (!winnerName) return null
    const match = slices.find((s) => extractName(s.text) === winnerName)
    return match ? { ...match, text: winnerName } : null
  })

  const onSpinStart = useCallback(() => {
    setResult(null)
    clearWinner()
  }, [])

  const onSpinComplete = useCallback((winningSlice) => {
    if (winningSlice) {
      const clean = { ...winningSlice, text: extractName(winningSlice.text) }
      setResult(clean)
      if (clean.text) saveWinner(clean.text)
    } else {
      setResult(null)
    }
  }, [])

  const handleDone = useCallback(() => {
    if (!result) return
    if (markDone(result.text)) {
      removeItem(result.text)
      setResult(null)
    }
  }, [result, removeItem])

  return (
    <>
      {isEmpty && <EmptyState context="wheel" />}
      <div className="rodg-wheel-wrapper">
        <ResultDisplay result={result} />
        <button
          className="btn-base btn-secondary rodg-done-btn"
          onClick={handleDone}
          style={{ visibility: result ? "visible" : "hidden" }}
        >
          Done ✓
        </button>
        <SpinWheel
          slices={slices}
          onSpinStart={isEmpty ? undefined : onSpinStart}
          onSpinComplete={isEmpty ? undefined : onSpinComplete}
          winnerIndex={null}
        />
      </div>
      <ColorsSection
        className="rodg-wheel-colors"
        choices={choices}
        customColors={customColors}
        onColorChange={handleColorChange}
        onResetAll={handleResetAllColors}
      />
    </>
  )
}

export default Wheel
